/**
 * @fileoverview Aba "Contato ativo" do editor de triagem: a conexão que abre a
 * conversa e, na API oficial, o template aprovado com a origem de cada variável
 * ({{1}}, {{2}}...). No WAHA não há template: a conversa abre com a mensagem de
 * abertura da aba Mensagens (a tela avisa do risco de bloqueio).
 */

import React from "react";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../../../translate/i18n";
import ModernSelect from "../../../../components/ModernSelect";
import useStyles from "./styles";
import { corpoDe, findTemplate, templateChoice, TemplateFields, templateProblem, useMetaTemplates } from "./metaTemplates";

const o = (key, params) => i18n.t(`triage.manager.editor.outreach.${key}`, params);

const OutreachEditor = ({ value, onChange, connections, removedConnection = false }) => {
  const classes = useStyles();
  const conexao = connections.find((c) => String(c.id) === String(value.whatsappId)) || null;
  const isMeta = conexao?.provider === "meta-api";
  const templates = useMetaTemplates(conexao?.id, isMeta);

  const escolhido = findTemplate(templates.list, value.template);

  const escolherTemplate = (tpl) => {
    if (templateProblem(tpl)) return;
    onChange({ ...value, template: templateChoice(tpl, value.template) });
  };

  return (
    <div className={classes.form}>
      <div className={classes.hint}>{o("intro")}</div>

      <ModernSelect
        className={classes.field}
        size="small"
        fullWidth
        label={o("connection")}
        helperText={o("connectionHint")}
        InputLabelProps={{ shrink: true }}
        displayEmpty
        value={value.whatsappId}
        onChange={(ev) => onChange({ whatsappId: ev.target.value, template: null })}
      >
        <MenuItem value="">
          <em>{o("connectionNone")}</em>
        </MenuItem>
        {connections.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            {`${c.name} · ${o(`providers.${c.provider}`)}`}
          </MenuItem>
        ))}
      </ModernSelect>
      {conexao && conexao.status !== "CONNECTED" && <div className={classes.warnText}>{o("disconnected")}</div>}
      {removedConnection && !conexao && <div className={classes.warnText}>{o("removedConnection")}</div>}

      {conexao && !isMeta && (
        <div className={classes.noteBox}>
          <div>{o("wahaInfo")}</div>
          <div className={classes.warnText}>{o("wahaWarning")}</div>
        </div>
      )}

      {isMeta && (
        <>
          <div>
            <div className={classes.label}>{o("template")}</div>
            <div className={classes.hint}>{o("templateHint")}</div>
          </div>
          {templates.loading ? (
            <div className={classes.hint} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <CircularProgress size={14} /> {o("loadingTemplates")}
            </div>
          ) : templates.error ? (
            <div className={classes.warnText}>{o("templatesError")}</div>
          ) : templates.list.length === 0 ? (
            <div className={classes.warnText}>{o("noTemplates")}</div>
          ) : (
            <div className={classes.tplList} role="listbox" aria-label={o("template")}>
              {templates.list.map((tpl) => {
                const problema = templateProblem(tpl);
                const ativo = escolhido && escolhido.name === tpl.name && escolhido.language === tpl.language;
                return (
                  <button
                    key={`${tpl.name}_${tpl.language}`}
                    type="button"
                    role="option"
                    aria-selected={!!ativo}
                    disabled={!!problema}
                    className={`${classes.tplItem} ${ativo ? classes.tplItemActive : ""}`}
                    onClick={() => escolherTemplate(tpl)}
                  >
                    <span className={classes.rowTitle}>
                      {tpl.name}
                      <span className={`${classes.chip} ${classes.chipOutline}`}>{tpl.language}</span>
                      {tpl.category && <span className={classes.chip}>{tpl.category}</span>}
                    </span>
                    <span className={classes.rowDesc}>{corpoDe(tpl)}</span>
                    {problema && <span className={classes.warnText}>{problema}</span>}
                  </button>
                );
              })}
            </div>
          )}
          {value.template && !escolhido && !templates.loading && !templates.error && templates.list.length > 0 && (
            <div className={classes.warnText}>{o("missingTemplate", { name: value.template.name })}</div>
          )}
          {!value.template && !templates.loading && templates.list.length > 0 && (
            <div className={classes.hint}>{o("chooseTemplate")}</div>
          )}

          <TemplateFields
            value={value.template}
            chosen={escolhido}
            onChange={(template) => onChange({ ...value, template })}
          />
          <div className={classes.hint}>{o("metaInfo")}</div>
        </>
      )}
    </div>
  );
};

export default OutreachEditor;
